import { useMemo } from "react"

import { isDerivedNpi } from "~/components/demand-2/demand/totalAggregations"
import type { EffectiveDemand } from "~/components/demand-2/demand/useEffectiveDemand"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { api } from "~/utils/api"
import { isPhasedOutVariant } from "~/utils/lifecycleUtils"

export type IsNewVariant = (v: IVariantSummary) => boolean

/**
 * `getNpiVariants` membership, phased-out excluded: on fashion, `soldInPreviousCollection`, the
 * Assortment tab's New vs Carry-over. Never `Sku.isNpi` once the server answers: it is undated and
 * only refreshed on ingestion; the cached flag is only a placeholder until the read resolves.
 */
export const useNpiVariants = (
  collectionIds: number[],
  kind: "fashion" | "noos",
  enabled = true,
) => {
  const npiQ = api.demand2.getNpiVariants.useQuery(
    { collectionIds, kind },
    { enabled: enabled && collectionIds.length > 0 },
  )
  const npiIds = useMemo(() => new Set((npiQ.data ?? []).map((v) => v.variantId)), [npiQ.data])
  const isNewVariant = useMemo<IsNewVariant>(
    () =>
      npiQ.isSuccess
        ? (v) => npiIds.has(v.variantId) && !isPhasedOutVariant(v)
        : (v) => v.isNpi === true,
    [npiQ.isSuccess, npiIds],
  )
  return { npiQ, isNewVariant }
}

/**
 * The Type column's New variant vs Carry-over. Fashion counts what Assortment counts; NOOS keeps the engine's
 * NPI predicate, which is also `getNpiVariants`' NOOS population.
 */
export const useIsNewVariant = (
  kind: "fashion" | "noos",
  collectionIds: number[],
  effective: (v: IVariantSummary) => EffectiveDemand,
): IsNewVariant => {
  const { isNewVariant } = useNpiVariants(collectionIds, kind, kind === "fashion")
  return useMemo(
    () => (kind === "fashion" ? isNewVariant : (v) => isDerivedNpi(v, effective)),
    [kind, isNewVariant, effective],
  )
}
